class GiftWithPurchaseManager {
  constructor () {
    this.configs = []
    this.initialized = false
  }

  init () {
    if (this.initialized) {
      return
    }

    try {
      const configElements = document.querySelectorAll('[data-gwp-config]')

      if (configElements.length === 0) {
        console.warn('GWP: No configuration found - retrying in 100ms')
        // Retry once after a short delay in case DOM is still parsing
        setTimeout(() => {
          const retryElements = document.querySelectorAll('[data-gwp-config]')
          if (retryElements.length > 0) {
            this.initialized = false
            this.init()
          } else {
            console.warn('GWP: Still no config found after retry - GWP will not be active')
          }
        }, 100)
        return
      }

      this.configs = Array.from(configElements).map(configElement => ({
        enabled: configElement.dataset.gwpEnabled === 'true',
        threshold: parseInt(configElement.dataset.gwpThreshold) || 6000,
        maxThreshold: parseInt(configElement.dataset.gwpMaxThreshold) || 0,
        productId: configElement.dataset.gwpProductId,
        variantId: configElement.dataset.gwpVariantId,
        campaignId: configElement.dataset.gwpCampaignId || 'default',
        mandatoryVariantIds: (configElement.dataset.gwpMandatoryVariantIds || '')
          .split(',').map(id => id.trim()).filter(id => id),
        messageLabel: configElement.dataset.gwpMessageLabel || '',
        message: configElement.dataset.gwpMessage || '',
      }))

      this.initialized = true
    } catch (err) {
      console.error('GWP: Init failed', err)
    }
  }

  isEnabled () {
    if (!this.initialized) this.init()
    const enabled = this.configs.length > 0 && this.configs.some(config => config.enabled && config.variantId)

    return enabled
  }

  dismissCampaign (campaignId) {
    try {
      sessionStorage.setItem(`gwp_dismissed_${campaignId}`, 'true')
    } catch (err) {}
  }

  isDismissed (campaignId) {
    try {
      return sessionStorage.getItem(`gwp_dismissed_${campaignId}`) === 'true'
    } catch (err) {
      return false
    }
  }

  clearDismissal (campaignId) {
    try {
      sessionStorage.removeItem(`gwp_dismissed_${campaignId}`)
    } catch (err) {}
  }

  hasMandatoryVariants (cartItems, mandatoryVariantIds) {
    if (!mandatoryVariantIds || mandatoryVariantIds.length === 0) {
      return true // No mandatory variants required
    }

    const cartVariantIds = cartItems
      .filter(item => item.properties?._gwp !== 'true')
      .map(item => String(item.variant_id))

    const allPresent = mandatoryVariantIds.every(requiredId =>
      cartVariantIds.includes(String(requiredId))
    )

    return allPresent
  }

  process (cartState) {
    if (!cartState?.items) {
      console.warn('GWP: Invalid cart state', cartState)
      return { toAdd: [], toRemove: [], toUpdate: [] }
    }

    try {
      let cartTotal = 0
      for (const item of cartState.items) {
        if (item.properties?._gwp !== 'true') {
          cartTotal += item.final_line_price || 0
        }
      }

      const existingGifts = this.findGWPItems(cartState.items)

      const giftsToRemove = []
      const giftsToAdd = []
      const giftsToUpdate = []

      // Track which campaigns already have gifts in cart and count duplicates
      const campaignsInCart = new Map() // Map<campaignId, array of gift items>

      for (const gift of existingGifts) {
        const campaign = gift.properties?._gwp_campaign || 'unknown'
        if (!campaignsInCart.has(campaign)) {
          campaignsInCart.set(campaign, [])
        }
        campaignsInCart.get(campaign).push(gift)
      }

      // Check existing gifts for removal or quantity correction
      for (const gift of existingGifts) {
        const giftCampaign = gift.properties?._gwp_campaign || 'unknown'
        const giftsForCampaign = campaignsInCart.get(giftCampaign) || []

        // If there are multiple GWP items for the same campaign, keep only the first one
        if (giftsForCampaign.length > 1) {
          const firstGift = giftsForCampaign[0]
          if (gift.key !== firstGift.key) {
            giftsToRemove.push(gift.key)
            continue
          }
        }

        // Ensure GWP items never have quantity > 1
        if (gift.quantity > 1) {
          giftsToUpdate.push({
            key: gift.key,
            quantity: 1,
          })
        }

        const giftThreshold = parseInt(gift.properties?._threshold) || 0
        const giftMaxThreshold = parseInt(gift.properties?._max_threshold) || 0

        // Find the config for this campaign
        const config = this.configs.find(c => c.campaignId === giftCampaign)

        // If campaign no longer exists in settings, remove the gift immediately
        if (!config) {
          giftsToRemove.push(gift.key)
          continue
        }

        // Check if mandatory variants are present
        const hasMandatory = this.hasMandatoryVariants(cartState.items, config.mandatoryVariantIds)

        const thresholdMet = cartTotal >= giftThreshold
        const maxThresholdExceeded = giftMaxThreshold > 0 && cartTotal > giftMaxThreshold

        if (!thresholdMet) {
          giftsToRemove.push(gift.key)
        } else if (maxThresholdExceeded) {
          giftsToRemove.push(gift.key)
        } else if (!hasMandatory) {
          giftsToRemove.push(gift.key)
        }
      }

      for (const config of this.configs) {
        if (!config.enabled) {
          continue
        }
        if (!config.variantId) {
          continue
        }
        if (campaignsInCart.has(config.campaignId)) {
          continue
        }

        const thresholdMet = cartTotal >= config.threshold
        const maxThresholdExceeded = config.maxThreshold > 0 && cartTotal > config.maxThreshold
        const hasMandatory = this.hasMandatoryVariants(cartState.items, config.mandatoryVariantIds)

        // Cart dropped below threshold — clear dismissal so the gift auto-adds again next time
        if (!thresholdMet) {
          this.clearDismissal(config.campaignId)
          continue
        }

        if (this.isDismissed(config.campaignId)) {
          continue
        }

        if (thresholdMet && !maxThresholdExceeded && hasMandatory) {
          const properties = {
            _gwp: 'true',
            _gwp_campaign: config.campaignId,
            _threshold: config.threshold.toString(),
            _max_threshold: config.maxThreshold.toString(),
            _hiddenFreeGift: 'true',
            _hiddenFreeGiftCookieless: 'true',
            _gwp_message_label: config.messageLabel || '',
            _gwp_message: config.message || '',
          }

          if (config.messageLabel && config.message) {
            properties[config.messageLabel] = config.message
          }

          giftsToAdd.push({
            id: config.variantId,
            quantity: 1,
            price: 0,
            properties,
          })
        }
      }

      return {
        toAdd: giftsToAdd,
        toRemove: giftsToRemove,
        toUpdate: giftsToUpdate,
      }
    } catch (err) {
      console.error('GWP: Process error', err)
      return { toAdd: [], toRemove: [], toUpdate: [] }
    }
  }

  findGWPItems (cartItems) {
    return cartItems.filter(item => item.properties?._gwp === 'true')
  }

  getState (cartState) {
    if (!this.isEnabled()) {
      return {
        enabled: false,
        campaigns: [],
      }
    }

    const cartTotal = cartState?.total_price || 0
    const gwpItems = this.findGWPItems(cartState?.items || [])

    // Calculate state for each campaign
    const campaignStates = this.configs.map(config => {
      const threshold = config.threshold
      const thresholdMet = cartTotal >= threshold
      const remaining = Math.max(0, threshold - cartTotal)
      const progress = Math.min(100, (cartTotal / threshold) * 100)

      // Check if this campaign has a gift in cart
      const hasGift = gwpItems.some(item =>
        item.properties?._gwp_campaign === config.campaignId,
      )

      return {
        campaignId: config.campaignId,
        enabled: config.enabled,
        thresholdMet,
        progress: Math.round(progress),
        remaining,
        threshold,
        maxThreshold: config.maxThreshold,
        hasGift,
      }
    })

    return {
      enabled: true,
      cartTotal,
      campaigns: campaignStates,
      totalGifts: gwpItems.length,
    }
  }
}

class GiftWithPurchaseElement extends window.BAO.CustomElement() {
  constructor () {
    super()
    this.handleTriggerClick = this.handleTriggerClick.bind(this)
    this.handleRemoveClick = this.handleRemoveClick.bind(this)
  }

  connectedCallback () {
    super.connectedCallback()
    this.setupTriggers()
  }

  setupTriggers () {
    const dataElement = this.querySelector('[data-gift-with-purchase-el="opt2_data"]')
    const triggerButton = this.querySelector('[data-gift-with-purchase-el="drawer_trigger"]')

    if (triggerButton && dataElement) {
      this.listeners.add(triggerButton, 'click', this.handleTriggerClick)
    }

    const removeButton = this.querySelector('[data-gift-with-purchase-el="remove"]')
    if (removeButton) {
      this.listeners.add(removeButton, 'click', this.handleRemoveClick)
    }
  }

  async handleRemoveClick (event) {
    event.preventDefault()

    const dataElement = this.querySelector('[data-gift-with-purchase-el="opt2_data"]')
    const campaignId = dataElement?.dataset?.campaignId

    if (!campaignId || !window.BAO.Cart) return

    try {
      const cart = await window.BAO.Cart.getCart()
      const gwpItem = cart.items.find(item =>
        item.properties?._gwp === 'true' &&
        item.properties?._gwp_campaign === campaignId,
      )

      if (gwpItem) {
        await window.BAO.Cart.removeItem(gwpItem.key)
      }
    } catch (err) {
      console.error('GWP [handleRemoveClick]: error', err)
    }
  }

  handleTriggerClick (event) {
    event.preventDefault()

    const dataElement = this.querySelector('[data-gift-with-purchase-el="opt2_data"]')

    if (!dataElement) return

    const detail = {
      productUrl: dataElement.dataset.productUrl,
      productId: dataElement.dataset.productId,
      messageLabel: dataElement.dataset.productMessageLabel,
      messageContent: dataElement.dataset.productMessage,
      customPriceText: dataElement.dataset.customPriceText,
      freeGiftStyling: dataElement.dataset.freeGiftStyling,
      threshold: dataElement.dataset.minCartValue,
      maxThreshold: dataElement.dataset.maxCartValue,
      campaignId: dataElement.dataset.campaignId,
      mandatoryVariantIds: dataElement.dataset.mandatoryVariantIds || '',
    }

    document.dispatchEvent(new CustomEvent('addGWP', { detail }))
  }
}

if (typeof window.customElements !== 'undefined') {
  window.customElements.define('gift-with-purchase', GiftWithPurchaseElement)
}

window.BAO = window.BAO || {}
window.BAO.GiftWithPurchase = new GiftWithPurchaseManager()

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', async () => {
    window.BAO.GiftWithPurchase.init()

    if (window.BAO.Cart) {
      try {
        const cart = await window.BAO.Cart.getCart()

        if (cart && cart.item_count > 0) {
          await window.BAO.Cart.processGiftWithPurchase(cart, { silent: false })
        }
      } catch (err) {
        console.error('GWP: Failed to check cart on load', err)
      }
    }
  })
} else {
  window.BAO.GiftWithPurchase.init()

  const waitForCart = async (retries = 10, delay = 100) => {
    if (window.BAO.Cart) {
      try {
        const cart = await window.BAO.Cart.getCart()

        if (cart && cart.item_count > 0) {
          await window.BAO.Cart.processGiftWithPurchase(cart, { silent: false })
        }
      } catch (err) {
        console.error('GWP: Failed to process cart on page load', err)
      }
    } else if (retries > 0) {
      setTimeout(() => waitForCart(retries - 1, delay), delay)
    }
  }

  waitForCart()
}
